import { useSelector } from "react-redux";
import { useCallback, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import {
  DisplayResultMessage,
  RequestSource,
  ResendRegistrationEmailRequest,
  VerifyConfirmEmailCodeRequest,
  LoginResponseWithUserId,
} from "@shared/types";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";

interface useVerifyEmailProps {
  emailValidatorUUID?: string | null;
  displayResultMessage: DisplayResultMessage;
  onSubmitSuccess?: (redirectToOAuth?: boolean) => void;
  onResendSuccess?: (emailValidatorUUID: string) => void;
  storageEmail?: string | null;
  mobileIdentifier?: string;
  uniqueIdentifier?: string;
}

const useVerifyEmail = ({
  emailValidatorUUID,
  displayResultMessage,
  onSubmitSuccess,
  onResendSuccess,
  storageEmail,
  mobileIdentifier,
  uniqueIdentifier,
}: useVerifyEmailProps) => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const useEmailValidatorUUID = !user && !!emailValidatorUUID;
  const [verifyConfirmEmailCode, verifyConfirmEmailCodeStatus] =
    sharedLib.sharedApi.authentication.useVerifyConfirmEmailCodeMutation();
  const [verifyConfirmEmailCodeByUUID, verifyConfirmEmailCodeByUUIDStatus] =
    sharedLib.sharedApi.authentication.useVerifyConfirmEmailCodeByUUIDMutation();
  const [resendRegistrationEmail, resendRegistrationEmailStatus] =
    sharedLib.sharedApi.authentication.useResendRegistrationEmailMutation();
  const [getUser, getUserStatus] =
    sharedLib.sharedApi.authentication.useLazyGetUserQuery();
  const [showSupportLink, setShowSupportLink] = useState(false);

  const email = user?.email || storageEmail;

  const onSubmit = useCallback(
    (code: string) => {
      if (code) {
        const request: VerifyConfirmEmailCodeRequest = {
          auth_code: parseInt(code),
          request_source: RequestSource.WEBSITE,
          mobile_identifier: mobileIdentifier,
          unique_identifier: uniqueIdentifier,
        };
        if (useEmailValidatorUUID) {
          request.email_validator_uuid = emailValidatorUUID;
        }
        const func = useEmailValidatorUUID
          ? verifyConfirmEmailCodeByUUID
          : verifyConfirmEmailCode;
        displayResultMessage(
          func(request as any).unwrap(),
          `Your email ${email} was verified successfully.`,
          "Wrong verification code. Please try again."
        )
          .then(async (response: LoginResponseWithUserId) => {
            const redirectToOAuth = handleUserTempOAuthData(response.user_id);
            onSubmitSuccess?.(redirectToOAuth);
            if (!useEmailValidatorUUID) {
              getUser();
            }
          })
          .catch(() => {});
      }
    },
    [
      emailValidatorUUID,
      useEmailValidatorUUID,
      mobileIdentifier,
      uniqueIdentifier,
    ]
  );

  const onResendCode = () => {
    setShowSupportLink(true);

    const request: ResendRegistrationEmailRequest = {
      request_source: RequestSource.WEBSITE,
    };
    if (useEmailValidatorUUID) {
      request.email_validator_uuid = emailValidatorUUID;
    } else {
      request.email = user?.email;
    }
    displayResultMessage(resendRegistrationEmail(request).unwrap())
      .then((response) => {
        const emailValidatorUUID = response.data.email_validator_uuid;
        if (useEmailValidatorUUID && emailValidatorUUID) {
          onResendSuccess?.(emailValidatorUUID);
        }
      })
      .catch(() => {});
  };

  const verificationCodeLoading =
    getUserStatus.isFetching ||
    verifyConfirmEmailCodeStatus.isLoading ||
    verifyConfirmEmailCodeByUUIDStatus.isLoading;
  const codeText = `Please provide the 6-digit code you received${
    email ? ` at ${email}.` : "."
  }`;

  return {
    onResendCode,
    onSubmit,
    showSupportLink,
    codeText,
    resendLoading: resendRegistrationEmailStatus.isLoading,
    verificationCodeLoading,
  };
};

export default useVerifyEmail;
